import { atom } from "jotai";
import Constants from "expo-constants";
import { Mission, TimeSheet, Cct, LppPlan, WorkingTimeClassicType } from "../__generated__/graphql";
import { Buildable } from "ts-essentials";
import { IjmSelection, MissionWithIjm } from "../utils";
import * as Sentry from "@sentry/react-native";

export const allMissionsState = atom<Mission[]>();

export const allTimesheetsState = atom<TimeSheet[]>();

type CctLoadContext = {
    stateCode: string;
    locale: string;
    startDate: string | null;
};

export const cctLoadContextState = atom<CctLoadContext>({
    stateCode: "",
    locale: "",
    startDate: null,
});

export const newMissionState = atom<Buildable<MissionWithIjm>>({});

// Last hoursPerWeekFullTime choosen by the user on CreateMissionWorkerTimeMore, used to re-adjust hours when switching CCTs.
export const lastValidatedWorkingTime = atom<{
    hoursPerWeekFullTime?: number;
    workingSpec?: WorkingTimeClassicType;
}>();

/**
 * This section is for CCT management jotai atoms and selector
 * The `allCCTState` atom should never be set manually, you must use the Hook useLoadCCTs ( cct-utils.ts )
 * All selectors usage based on allCCTState must call the useLoadCCTs hook first
 *
 * TODO: All this CCT management should be re-written to use the Apollo cache instead of jotai
 * Note that the allCct list is based on a contract address state, we should probably use a currentContract atom as source
 * element and use selectors to derives the allCCT list from it
 * Event a ContextManager could be used to manage the current contract and allCCT list
 */
export const allCCTState = atom<Cct[]>();

export const defaultCCTState = atom<Cct | null>((get) => {
    const allCCT = get(allCCTState);
    const matchingCCT = allCCT?.filter((cct) => {
        return cct.cctContractId === Constants.expoConfig?.extra?.cctDefaultContractId;
    });
    if (matchingCCT && matchingCCT.length > 0) {
        return matchingCCT[0];
    } else {
        return null;
    }
});

export const liftBasedNoCCTState = atom<Cct | null>((get) => {
    const allCCT = get(allCCTState);
    const matchingCCT = allCCT?.filter((cct) => {
        return cct.cctContractId.startsWith("lift-no-cct");
    });
    if (matchingCCT && matchingCCT.length > 0) {
        if (matchingCCT.length > 1) {
            Sentry.captureMessage("More than 1 no CCT found in the list of CCTs", "warning");
        }
        return matchingCCT[0];
    } else {
        return null;
    }
});

type editIjmSelectionCallbackType = {
    func?: (selection: IjmSelection | null) => void | Promise<void>;
};

export const editIjmSelectionCallback = atom<editIjmSelectionCallbackType>({ func: undefined });

type editLppPlanCallbackType = {
    func?: (plan?: LppPlan) => void | Promise<void>;
};

export const editLppPlanCallback = atom<editLppPlanCallbackType>({ func: undefined });

type editVacationCallbackType = {
    func?: (result: {
        vacationDays?: number;
        payVacationIndemnityWithSalary: boolean;
    }) => void | Promise<void>;
};

export const editVacationCallback = atom<editVacationCallbackType>({ func: undefined });

export type ThirteenthSalaryEditorResult = {
    has13thSalary: boolean;
    pay13thSalaryWithSalary: boolean;
};

type editThirteenthSalaryCallbackType = {
    func?: (result: ThirteenthSalaryEditorResult) => void | Promise<void>;
};

export const editThirteenthSalaryCallback = atom<editThirteenthSalaryCallbackType>({
    func: undefined,
});
